import { useState, useEffect, useRef, useCallback } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { PresenceUser, CursorPosition, CommentPinData, DocCommentData, DocumentProjectDetail } from '@corner/shared';

// ─── Types ──────────────────────────────────────

export interface UseCollaborationOptions {
  /** The conversation/document ID to join */
  conversationId: string | null;
  /** JWT access token for socket auth */
  getToken: () => string | null;
  /** Whether collaboration is enabled (e.g., user is authenticated) */
  enabled?: boolean;
}

export interface UseCollaborationReturn {
  /** List of users currently in the document room */
  presenceUsers: PresenceUser[];
  /** Map of remote cursors (keyed by userId) */
  remoteCursors: Map<string, CursorPosition>;
  /** Send own cursor position to other users */
  sendCursorPosition: (x: number, y: number, pageNumber: number) => void;
  /** Whether the socket is connected */
  connected: boolean;
  /** Real-time comment events — subscribe to these for live updates */
  onCommentEvent: (handler: CommentEventHandler) => void;
  /** Real-time project/version events — subscribe to shared version updates */
  onProjectEvent: (handler: ProjectEventHandler) => void;
}

export type CommentEvent =
  | { type: 'created'; pin: CommentPinData }
  | { type: 'reply'; pinId: string; comment: DocCommentData }
  | { type: 'resolved'; pinId: string }
  | { type: 'moved'; pinId: string; xPercent: number; yPercent: number }
  | { type: 'deleted'; pinId: string };

export type CommentEventHandler = (event: CommentEvent) => void;

export type ProjectEvent =
  | { type: 'version_updated'; project: DocumentProjectDetail };

export type ProjectEventHandler = (event: ProjectEvent) => void;

// ─── Throttle helper ────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function throttle<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let lastCall = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return ((...args: any[]) => {
    const now = Date.now();
    if (now - lastCall >= ms) {
      lastCall = now;
      fn(...args);
    }
  }) as T;
}

// ─── Hook ───────────────────────────────────────

export function useCollaboration({
  conversationId,
  getToken,
  enabled = true,
}: UseCollaborationOptions): UseCollaborationReturn {
  const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);
  const [remoteCursors, setRemoteCursors] = useState<Map<string, CursorPosition>>(new Map());
  const [connected, setConnected] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  // Comment event handler ref — allows consumers to subscribe
  const commentHandlerRef = useRef<CommentEventHandler | null>(null);
  const projectHandlerRef = useRef<ProjectEventHandler | null>(null);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log('[Collab] Hook mounted. VITE_SOCKET_URL=' + (import.meta.env.VITE_SOCKET_URL ?? 'undefined') +
      ' VITE_API_URL=' + (import.meta.env.VITE_API_URL ?? 'undefined') +
      ' enabled=' + enabled + ' conversationId=' + (conversationId ?? 'null'));
  }, [enabled, conversationId]);

  // Connect socket — uses ref for getToken to avoid dependency issues
  useEffect(() => {
    if (!enabled || !conversationId) return;

    const token = getTokenRef.current();
    if (!token) return;

    // Socket URL: use Vite proxy in dev, VITE_SOCKET_URL in prod.
    // Vercel rewrites only proxy HTTP — WebSockets must connect directly to the backend.
    // Falls back to VITE_API_URL (same origin as API) if VITE_SOCKET_URL is not set.
    const socketUrl = import.meta.env.DEV
      ? `http://localhost:${import.meta.env.VITE_API_PORT ?? 3001}`
      : (import.meta.env.VITE_SOCKET_URL ?? import.meta.env.VITE_API_URL ?? null);

    if (import.meta.env.DEV) {
      console.log('[Collab] Socket setup: url=' + (socketUrl ?? 'NULL') + ' convId=' + conversationId + ' hasToken=' + !!token);
    }
    if (!socketUrl) {
      if (import.meta.env.DEV) console.log('[Collab] No socket URL — skipping');
      return;
    }

    const socket = io(socketUrl, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      if (import.meta.env.DEV) console.log('[Collab] Connected! Joining room:', conversationId);
      setConnected(true);
      socket.emit('join-document', { conversationId });
      conversationIdRef.current = conversationId;
    });

    socket.on('connect_error', (err) => {
      console.error('[Collab] Connection error:', err.message);
    });

    socket.on('disconnect', () => {
      setConnected(false);
    });

    // ── Presence events ──
    socket.on('presence:list', (users: PresenceUser[]) => {
      if (import.meta.env.DEV) console.log('[Collab] Presence list received:', users.length, 'users');
      setPresenceUsers(users);
    });

    socket.on('presence:join', (user: PresenceUser) => {
      if (import.meta.env.DEV) console.log('[Collab] Presence join:', user.displayName);
      setPresenceUsers((prev) => {
        const filtered = prev.filter((u) => u.userId !== user.userId);
        return [...filtered, user];
      });
    });

    socket.on('presence:leave', ({ userId }: { userId: string }) => {
      setPresenceUsers((prev) => prev.filter((u) => u.userId !== userId));
      setRemoteCursors((prev) => {
        const next = new Map(prev);
        next.delete(userId);
        return next;
      });
    });

    // ── Cursor events — keyed by userId, one cursor per user ──
    socket.on('cursor:update', (cursor: CursorPosition) => {
      setRemoteCursors((prev) => {
        const next = new Map(prev);
        next.set(cursor.userId, cursor);
        return next;
      });
    });

    // ── Comment real-time events ──
    socket.on('comment:created', (pin: CommentPinData) => {
      commentHandlerRef.current?.({ type: 'created', pin });
    });
    socket.on('comment:reply', (data: { pinId: string; comment: DocCommentData }) => {
      commentHandlerRef.current?.({ type: 'reply', pinId: data.pinId, comment: data.comment });
    });
    socket.on('comment:resolved', (data: { pinId: string }) => {
      commentHandlerRef.current?.({ type: 'resolved', pinId: data.pinId });
    });
    socket.on('comment:moved', (data: { pinId: string; xPercent: number; yPercent: number }) => {
      commentHandlerRef.current?.({ type: 'moved', pinId: data.pinId, xPercent: data.xPercent, yPercent: data.yPercent });
    });
    socket.on('comment:deleted', (data: { pinId: string }) => {
      commentHandlerRef.current?.({ type: 'deleted', pinId: data.pinId });
    });

    socket.on('project:version_updated', (data: { project: DocumentProjectDetail }) => {
      if (data?.project) projectHandlerRef.current?.({ type: 'version_updated', project: data.project });
    });

    return () => {
      if (conversationIdRef.current) {
        socket.emit('leave-document');
      }
      socket.disconnect();
      socketRef.current = null;
      conversationIdRef.current = null;
      setPresenceUsers([]);
      setRemoteCursors(new Map());
      setConnected(false);
    };
  }, [conversationId, enabled]); // getToken accessed via ref — no dependency needed

  // Handle conversationId changes while already connected
  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !connected || !conversationId) return;

    if (conversationIdRef.current !== conversationId) {
      if (conversationIdRef.current) {
        socket.emit('leave-document');
      }
      socket.emit('join-document', { conversationId });
      conversationIdRef.current = conversationId;
      setPresenceUsers([]);
      setRemoteCursors(new Map());
    }
  }, [conversationId, connected]);

  // Throttled cursor position sender (30fps = 33ms for smooth remote cursor movement)
  const sendCursorPosition = useCallback(
    throttle((x: number, y: number, pageNumber: number) => {
      const socket = socketRef.current;
      const convId = conversationIdRef.current;
      if (!socket || !convId) return;
      socket.emit('cursor:move', { conversationId: convId, x, y, pageNumber });
    }, 33),
    [],
  );

  // Subscribe to comment events
  const onCommentEvent = useCallback((handler: CommentEventHandler) => {
    commentHandlerRef.current = handler;
  }, []);

  const onProjectEvent = useCallback((handler: ProjectEventHandler) => {
    projectHandlerRef.current = handler;
  }, []);

  return { presenceUsers, remoteCursors, sendCursorPosition, connected, onCommentEvent, onProjectEvent };
}
